import { ElementType, InputHTMLAttributes } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons'
import * as Select from '@radix-ui/react-select'

import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { isCorporate } from '@/constants'

import { cn } from '@/lib/utils'
import { WPGravityFormField } from '@/types'

type FormControlProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'onBlur'
> &
  Partial<
    WPGravityFormField & {
      error?: boolean
      size: 'small' | 'large' | 'medium'
      options?: { value: string; text: string }[]
      // eslint-disable-next-line no-unused-vars
      onChange?: (e: any) => void
      // eslint-disable-next-line no-unused-vars
      onBlur?: (e: any) => void
      theme?: 'dark' | 'light'
    }
  >

const FormControl = ({
  id,
  type,
  label,
  checked,
  options,
  placeholder,
  className,
  children,
  theme = 'light',
  error,
  size,
  ...props
}: FormControlProps) => {
  const Component = (type === 'textarea' ? 'textarea' : 'input') as ElementType

  return (
    <div
      className={cn(
        className,
        {
          hidden: type === 'hidden'
        },
        {
          'col-span-2 lg:col-span-1': size === 'medium',
          'col-span-2': size === 'large'
        }
      )}
    >
      {type === 'consent' ? (
        <label className="relative flex items-center space-x-[1rem]">
          <input
            id={id}
            type="checkbox"
            checked={checked}
            className={cn([
              'size-[2.4rem] opacity-0 [&:checked+div_svg]:opacity-100',
              {
                '[&:checked+div]:after:bg-border-primary-light':
                  theme === 'light',
                '[&:checked+div]:after:bg-border-primary-dark': theme === 'dark'
              }
            ])}
            {...props}
          />
          <div
            className={cn([
              'absolute left-0 top-0 !m-0 size-[2.4rem] shrink-0 cursor-pointer rounded-[0.4rem] border',
              {
                'border-border-menu-light': theme === 'light',
                'border-border-menu-inverted-light': theme === 'dark',
                'border-error': error
              }
            ])}
          >
            <SVGIcon
              symbol="check-icon"
              className={cn([
                'absolute left-1/2 top-1/2 z-10 size-[1.6rem] -translate-x-1/2 -translate-y-1/2 fill-surface-icon-light opacity-0 transition-opacity',
                {
                  'fill-surface-icon-dark': theme === 'light' && isCorporate,
                  'fill-surface-icon-light': theme === 'dark' && isCorporate
                }
              ])}
            />
          </div>
          <Typography
            variant="small"
            className={cn([
              '[&>#required-data]:mt-[1rem] [&>#required-data]:inline-block',
              {
                'text-error': error
              }
            ])}
          >
            {label}
          </Typography>
        </label>
      ) : type === 'select' ? (
        <Select.Root
          value={props.value as string}
          onValueChange={props.onChange}
        >
          <Select.Trigger
            id={id}
            className={cn([
              'text-body flex w-full appearance-none items-center justify-between bg-transparent px-[3rem] py-[1.5rem] outline-none',
              'border',
              'focus:ring-2',
              {
                'text-dark border-black/15': theme === 'light',
                'border-white/20 text-white': theme === 'dark',
                'text-black/15': !props.value && theme === 'light',
                'text-white/20': !props.value && theme === 'dark'
              },
              {
                'focus:border-black/80 focus:ring-transparent':
                  !error && theme === 'light',
                'focus:border-white/80 focus:ring-transparent':
                  !error && theme === 'dark',
                'border-error focus:ring-error/50': error
              }
            ])}
          >
            <Select.Value placeholder={placeholder} />
            <Select.Icon>
              <ChevronDownIcon
                className={cn([
                  {
                    'text-dark-500': theme === 'light',
                    'text-white': theme === 'dark'
                  }
                ])}
              />
            </Select.Icon>
          </Select.Trigger>

          <Select.Portal>
            <Select.Content
              data-lenis-prevent
              className={cn([
                'z-50 overflow-hidden rounded-[0.4rem] border shadow-md',
                {
                  'border-black/15 bg-white': theme === 'light',
                  'border-white/20 bg-dark-500': theme === 'dark'
                }
              ])}
            >
              <Select.ScrollUpButton className="flex h-[25px] items-center justify-center">
                <ChevronUpIcon />
              </Select.ScrollUpButton>

              <Select.Viewport>
                {options?.map((option) => (
                  <Select.Item
                    key={option.value}
                    value={option.value}
                    className={cn([
                      'text-body relative flex select-none items-center px-[2rem] py-[0.75rem] outline-none',
                      'data-[highlighted]:bg-gray-100 hover:cursor-pointer',
                      {
                        'text-dark-500': theme === 'light',
                        'text-white hover:data-[highlighted]:bg-dark-400':
                          theme === 'dark'
                      }
                    ])}
                  >
                    <Select.ItemText>{option.text}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>

              <Select.ScrollDownButton className="flex h-[25px] items-center justify-center">
                <ChevronDownIcon />
              </Select.ScrollDownButton>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      ) : (
        <Component
          id={id}
          type={type}
          placeholder={placeholder}
          className={cn([
            'flex w-full appearance-none rounded-[0.4rem] bg-transparent p-spacing-xs font-roboto text-regular outline-none',
            'border',
            'focus:ring-2',
            {
              'border-border-menu-light text-text-primary-light placeholder:text-text-primary-light/15':
                theme === 'light',
              'border-border-menu-inverted-light text-text-heading-invert-light placeholder:text-text-primary-invert-light':
                theme === 'dark'
            },
            {
              'border-error focus:ring-error/50': error
            }
          ])}
          {...props}
        />
      )}
      {children}
    </div>
  )
}

export default FormControl
